import React, { FC, useContext, MouseEvent, useState } from 'react';
import { useSelector } from 'react-redux';
import { Text, Heading6 } from '@blueprint/ui';
import { useRouter } from 'next/router';
import triggerTracking from '@blueprint/tracking';

import { SelectPropertyType } from './SelectPropertyType';
import { SelectBeds } from './SelectBeds';
import { SelectPriceForSale } from './SelectPriceForSale';
import { SelectPriceToRent } from './SelectPriceToRent';
import { SelectMaxTravelTime } from './SelectMaxTravelTime';
import { SelectTransportMode } from './SelectTransportMode';
import {
    SearchContext,
    SearchPageType,
    SearchSource
} from '../../../search/SearchContext';
import { CloseIconWithText } from '../../../search/common';
import { LocationSearchInput } from '../LocationSearchInput/LocationSearchInput';
import {
    filtersValuesSelector,
    isTravelTimeSearchSelector,
    locationSelector,
    identifierSelector,
    locationTextSelector
} from '../../../reducers/filters';
import { selectCurrentSortOrder } from '../../../reducers/listingsSortOrder';
import {
    HeaderWrapper,
    ModalCloseIconWrapper,
    UpdateButton,
    LocationFilterWrapper,
    ModalContentWrapper,
    FindHomeTextWrapper,
    StickyWrapperUpdateButton,
    FilterDivider
} from './SearchInitiationMobileModal.styles';
import { BuyRentTabs } from '../../../homepage/components';
import { Tab } from '../../../homepage/components/HeroContainer/HeroContainer.types';

import { SelectRadius } from './SelectRadius';
import {
    SearchInitationWrapper,
    StyledButtonMobile,
    StyledButtonWrapperMobile,
    HousePricesLocationWrapper
} from '../SearchInitiation/SearchInitiation.styles';
import { getTranslator } from '../../i18n/getTranslator';
import searchTranslations from 'public/locales/en/search.json';
import { searchHousePrices, searchProperties } from 'search/helpers';
import homepageTranslations from 'public/locales/en/homepage.json';

const t = getTranslator(searchTranslations);

const th = getTranslator(homepageTranslations);

const mobileModalHomeWorthText = th('mobile_modal.home_worth');

const SEARCH_BUTTON_TEXT = t('filters.search');
const EVENT = 'GAEvent';
const CATEGORY = 'search_filters';

type Props = {
    showRadius?: boolean;
    enableSearchTypeTabs?: boolean;
    updateResultsText: string;
    headlineText?: string;
    close: () => void;
    searchType: Tab;
    eventCategory: 'homepage' | 'search results';
    searchSource: SearchSource;
    excludePageSize?: boolean;
};

export const SearchInitiationMobileModal: FC<Props> = (props) => {
    const {
        showRadius = true,
        close,
        enableSearchTypeTabs = true,
        updateResultsText,
        headlineText,
        searchType,
        eventCategory,
        searchSource,
        excludePageSize = false
    } = props;
    const router = useRouter();
    const { state, dispatch } = useContext(SearchContext);
    const { userAlertId } = state;

    const { selectedValue: locationValue } = useSelector(locationSelector);
    const { selectedValue: identifier } = useSelector(identifierSelector);
    const { selectedValue: locationTextValue } = useSelector(
        locationTextSelector
    );
    const [
        hasSubmittedLocationSearch,
        setHasSubmittedLocationSearch
    ] = useState(false);
    const currentSortOrder = useSelector(selectCurrentSortOrder);
    const filters = useSelector(filtersValuesSelector);
    const isTravelTimeSearch = useSelector(isTravelTimeSearchSelector);

    const [tabSelected, setTabSelected] = useState<Tab>(searchType);

    const isForSale = enableSearchTypeTabs
        ? tabSelected === 'for-sale' || tabSelected === 'new-homes'
        : searchType === 'for-sale' || searchType === 'new-homes';
    const isHousePricesSearch = tabSelected === 'house-prices';

    const value = locationTextValue || locationValue;

    const handleSearchProperties = () => {
        const finalSearchType = enableSearchTypeTabs ? tabSelected : searchType;
        const hasEnteredAValueOrSelectedFromDropdown = !!value;
        const eventLabel = hasEnteredAValueOrSelectedFromDropdown
            ? `${finalSearchType}-success`
            : `${finalSearchType}-empty-search`;

        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            eventLabel
        });

        const excludeRadius = !showRadius;

        if (!value) {
            setHasSubmittedLocationSearch(true);
            return;
        }

        searchProperties(
            userAlertId,
            router,
            dispatch,
            finalSearchType as SearchPageType,
            searchSource,
            filters,
            currentSortOrder,
            excludeRadius,
            excludePageSize
        );

        close();
    };

    const headerText = isForSale
        ? 'Search homes for sale'
        : 'Search homes to rent';

    const handlePressUpdate = (evt: MouseEvent<HTMLButtonElement>) => {
        evt.preventDefault();
        evt.stopPropagation();

        triggerTracking({
            event: EVENT,
            eventCategory: CATEGORY,
            eventAction: 'click',
            eventLabel: 'quick_search_bar_update'
        });

        handleSearchProperties();
    };

    const handleSearchHousePrices = () => {
        triggerTracking({
            event: 'GAEvent',
            eventCategory,
            eventAction: 'search',
            // No failure label as the button is disabled if there is no content
            eventLabel: 'house-prices-success'
        });

        searchHousePrices(value, identifier);
        close();
    };

    const getHeadlineText = (tab: Tab) => {
        if (tab === 'house-prices') {
            return mobileModalHomeWorthText;
        } else {
            return headlineText || headerText;
        }
    };

    const handleClose = () => {
        triggerTracking({
            event: EVENT,
            eventCategory: CATEGORY,
            eventAction: 'click',
            eventLabel: 'quick_search_bar_close'
        });

        close();
    };

    const showError = hasSubmittedLocationSearch && !value;

    return (
        <ModalContentWrapper>
            <HeaderWrapper>
                {!headlineText ? (
                    <Text isBold color="grey-slate">
                        {headerText}
                    </Text>
                ) : null}
                <ModalCloseIconWrapper>
                    <CloseIconWithText
                        onClick={handleClose}
                        testId="quicksearch-modal_close-button_icon-only"
                        text="Close"
                    />
                </ModalCloseIconWrapper>
            </HeaderWrapper>

            {enableSearchTypeTabs ? (
                <FindHomeTextWrapper>
                    <Heading6>{getHeadlineText(tabSelected)}</Heading6>
                </FindHomeTextWrapper>
            ) : null}

            {enableSearchTypeTabs ? (
                <BuyRentTabs
                    onPress={setTabSelected}
                    selected={tabSelected}
                    includeHousePrices
                    testIdSuffix={'mobile-modal'}
                />
            ) : null}

            {isHousePricesSearch ? (
                <SearchInitationWrapper hasExtraPaddingTop>
                    <HousePricesLocationWrapper>
                        <LocationSearchInput
                            id="header-location"
                            onLocationInputKeyDown={handleSearchHousePrices}
                            hasBorderRight={false}
                            searchType="house-prices"
                            value={locationValue}
                        />
                    </HousePricesLocationWrapper>
                    <StyledButtonWrapperMobile>
                        <StyledButtonMobile
                            type="button"
                            onClick={handleSearchHousePrices}
                            data-testid="mobile-house-prices-search-button"
                            disabled={!value}
                        >
                            {SEARCH_BUTTON_TEXT}
                        </StyledButtonMobile>
                    </StyledButtonWrapperMobile>
                </SearchInitationWrapper>
            ) : (
                <>
                    <LocationFilterWrapper>
                        <LocationSearchInput
                            id="filters-location_mobile_modal"
                            onLocationInputKeyDown={handleSearchProperties}
                            onlyDisplayRemoveIconWhenActive={false}
                            value={locationValue}
                            showError={showError}
                        />
                    </LocationFilterWrapper>

                    <FilterDivider />

                    {isTravelTimeSearch ? (
                        <>
                            <SelectMaxTravelTime />
                            <FilterDivider />
                            <SelectTransportMode />{' '}
                        </>
                    ) : showRadius ? (
                        <SelectRadius />
                    ) : null}

                    <FilterDivider />

                    {isForSale ? <SelectPriceForSale /> : <SelectPriceToRent />}

                    <FilterDivider />

                    <SelectBeds />

                    <FilterDivider />

                    <SelectPropertyType />
                    <StickyWrapperUpdateButton>
                        <UpdateButton
                            isFullWidth
                            type="button"
                            onClick={handlePressUpdate}
                            data-testid="update-results-button"
                            title="Update"
                        >
                            {updateResultsText}
                        </UpdateButton>
                    </StickyWrapperUpdateButton>
                </>
            )}
        </ModalContentWrapper>
    );
};
